import {Action} from '@reduxjs/toolkit'
import {persistReducer} from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import {put, takeLatest, call} from 'redux-saga/effects'
import {UserModel} from '../models/UserModel'
import {SagaIterator} from 'redux-saga'
import {identityLoginUrl} from '../../../../setup/appConfig'

import {
  getUserByToken,
  getUserFromIdentity,
  refreshAccessToken,
  isTokenExpired,
} from './AuthCRUD'

export interface ActionWithPayload<T> extends Action {
  payload?: T
}

export const actionTypes = {
  Login: '[Login] Action',
  Logout: '[Logout] Action',
  Register: '[Register] Action',
  UserRequested: '[Request User] Action',
  UserLoaded: '[Load User] Auth API',
  SetUser: '[Set User] Action',
  GetUser: '[Get User] Action',
  SetAccessToken: '[Set Access Token] Action',
}

const initialAuthState: IAuthState = {
  user: undefined,
  accessToken: undefined,
}

export interface IAuthState {
  user?: UserModel
  accessToken?: string
}

export const reducer = persistReducer(
  {
    storage,
    key: 'v100-demo2-auth',
    whitelist: [], // 🚨 critical fix
  },
  (state: IAuthState = initialAuthState, action: ActionWithPayload<IAuthState>) => {
    switch (action.type) {
      case actionTypes.Login: {
        return {
          ...state,
          accessToken: action.payload?.accessToken,
          user: undefined,
        }
      }

      case actionTypes.Register: {
        return {
          ...state,
          accessToken: action.payload?.accessToken,
          user: undefined,
        }
      }

      case actionTypes.Logout: {
        return initialAuthState
      }

      case actionTypes.UserLoaded: {
        return {
          ...state,
          user: action.payload?.user,
        }
      }

      case actionTypes.SetAccessToken: {
        return {
          ...state,
          accessToken: action.payload?.accessToken,
        }
      }

      default:
        return state
    }
  }
)

// -------------------- ACTIONS --------------------

export const actions = {
  login: (accessToken: string) => ({
    type: actionTypes.Login,
    payload: {accessToken},
  }),

  logout: () => ({
    type: actionTypes.Logout,
  }),

  requestUser: () => ({type: actionTypes.UserRequested}),

  fulfillUser: (user: UserModel) => ({
    type: actionTypes.UserLoaded,
    payload: {user},
  }),

  getUser: () => ({type: actionTypes.GetUser}),

  setAccessToken: (accessToken: string) => ({
    type: actionTypes.SetAccessToken,
    payload: {accessToken},
  }),
}

// -------------------- HELPERS --------------------

function forceLogout() {
  localStorage.removeItem('accessToken')
  localStorage.removeItem('persist:v100-demo2-auth')
  sessionStorage.clear()

  window.location.href =
    identityLoginUrl()
}

// -------------------- SAGA --------------------

export function* saga(): SagaIterator {
  yield takeLatest(actionTypes.Login, function* () {
    yield put(actions.requestUser())
  })

  yield takeLatest(actionTypes.Register, function* () {
    yield put(actions.requestUser())
  })

  yield takeLatest(actionTypes.GetUser, function* (): SagaIterator {
    let accessToken: string | null = localStorage.getItem('accessToken')

    // 1. Identity login (nema tokena)
    if (!accessToken) {
      const {accessToken: identityToken}: any = yield call(getUserFromIdentity)

      accessToken = identityToken || null

      if (accessToken) {
        localStorage.setItem('accessToken', accessToken)
      }
    }

    // 2. Refresh ako je expired
    else if (isTokenExpired(accessToken)) {
      let refreshResponse: any

      try {
        refreshResponse = yield call(refreshAccessToken)
      } catch (e) {
        refreshResponse = null
      }

      if (refreshResponse?.accessToken) {
        accessToken = refreshResponse.accessToken
        if (accessToken) {
          localStorage.setItem('accessToken', accessToken)
        }
      } else {
        yield put(actions.logout())
        forceLogout()
        return
      }
    }

    // 3. Final guard
    if (!accessToken) {
      yield put(actions.logout())
      forceLogout()
      return
    }

    // 4. Load user
    const userData: UserModel = getUserByToken(accessToken)

    yield put(actions.login(accessToken))
    yield put(actions.fulfillUser(userData))
  })
}
